<div class="container-fluid">
  <h2 class="header-title">
    {{ tab_name }}
    <i class="bi bi-info-circle info-icon" (click)="openHeaderInfo()"
       [matTooltip]="isCustomTheme ? 'View query' : 'About Quant IP Clusters'"></i>
  </h2>

  <ng-template #queryDialogTpl>
    <h3 mat-dialog-title>Custom Theme Query</h3>
    <mat-dialog-content>
      <pre class="query-text">{{ query }}</pre>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button [cdkCopyToClipboard]="query || ''">Copy</button>
      <button mat-button mat-dialog-close>Close</button>
    </mat-dialog-actions>
  </ng-template>

  <!-- Spinner -->
  <div class="d-flex justify-content-center my-4" *ngIf="showSpinner">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <!-- Error -->
  <div class="alert alert-danger" *ngIf="loadingFailed">
    Failed to load executive summary. Please try again or contact
    <a href="mailto:info@quant-ip.com">info&#64;quant-ip.com</a>.
  </div>

  <!-- No data -->
  <div class="empty-state" *ngIf="showDataMissing && !showSpinner">
    <i class="bi bi-search empty-state-icon"></i>
    <h5>No Data Available</h5>
    <p>Executive summary data is not available for this theme.</p>
  </div>

  <!-- Main content -->
  <ng-container *ngIf="data && !showSpinner">

    <!-- ── Current Stage card ── -->
    <div class="chart-card">
      <h5>Current Stage <i class="bi bi-info-circle info-icon"
           (click)="openInfoPopup('theme_stages')"></i>:</h5>  

      <div class="s-curve-row">
        <!-- SVG S-Curve -->
        <div class="s-curve-container">
          <svg viewBox="0 0 600 220" class="s-curve-svg" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <clipPath [attr.id]="'seg-clip-' + tabId">
                <rect [attr.x]="getSegClipX()" y="0" [attr.width]="getSegClipWidth()" height="220" />
              </clipPath>
            </defs>

            <!-- Full gray curve -->
            <path [attr.d]="curvePath"
                  fill="none" stroke="#d1d5db" stroke-width="4" stroke-linecap="round" />

            <!-- Highlighted segment in stage color -->
            <path [attr.d]="curvePath"
                  fill="none" [attr.stroke]="highlightColor" stroke-width="5" stroke-linecap="round"
                  [attr.clip-path]="'url(#seg-clip-' + tabId + ')'" />

            <!-- Dotted vertical from current dot to label -->
            <line *ngIf="currentStageIndex >= 0"
                  [attr.x1]="getStageCx(currentStageIndex)" [attr.y1]="getStageCy(currentStageIndex)"
                  [attr.x2]="getStageCx(currentStageIndex)" y2="185"
                  [attr.stroke]="highlightColor" stroke-width="1" stroke-dasharray="4,3" />

            <!-- Current stage dot -->
            <circle *ngIf="currentStageIndex >= 0"
                    [attr.cx]="getStageCx(currentStageIndex)"
                    [attr.cy]="getStageCy(currentStageIndex)"
                    r="8" [attr.fill]="highlightColor" stroke="#fff" stroke-width="2.5" />

            <!-- Small dots on other stages -->
            <ng-container *ngFor="let stage of stages; let i = index">
              <circle *ngIf="i !== currentStageIndex"
                      [attr.cx]="getStageCx(i)" [attr.cy]="getStageCy(i)"
                      r="4" fill="#d1d5db" />
            </ng-container>

            <!-- Stage labels -->
            <text *ngFor="let stage of stages; let i = index"
                  [attr.x]="getStageCx(i)" y="200" text-anchor="middle"
                  [attr.font-weight]="i === currentStageIndex ? '700' : '400'"
                  [attr.fill]="i === currentStageIndex ? getStageColor(stage.name) : '#6b7280'"
                  font-size="14">{{ stage.name }}</text>

            <!-- Underline for current stage label -->
            <line *ngIf="currentStageIndex >= 0"
                  [attr.x1]="getStageCx(currentStageIndex) - 40"
                  [attr.x2]="getStageCx(currentStageIndex) + 40"
                  y1="206" y2="206"
                  [attr.stroke]="highlightColor" stroke-width="2" />
          </svg>
        </div>

        <!-- Callout box -->
        <div class="callout-box" [style.border-color]="highlightColor">
          <span class="callout-label">CURRENT STAGE</span>
          <span class="callout-stage-name" [style.color]="highlightColor">
            {{ data.current_stage_name || 'No stage' }}
          </span>
        </div>
      </div>

      <!-- Performance sentence -->
      <ng-container *ngIf="!isNaStage">
        <p class="performance-sentence">
          Stocks of the
          <a class="sentence-link" (click)="openStageCompaniesDialog()">top companies</a>
          that are most exposed to technologies at the
          <strong [style.color]="highlightColor">{{ data.current_stage_name }}</strong>
          Stage have historically
          <a class="sentence-link" (click)="openInfoPopup('theme_backtest')"
             [class.text-perf-positive]="data.performance_sentence.direction === 'outperformed'"
             [class.text-perf-negative]="data.performance_sentence.direction === 'underperformed'">
            {{ data.performance_sentence.direction }} the market by
            {{ data.performance_sentence.spread_pct > 0 ? '+' : '' }}{{ data.performance_sentence.spread_pct }}% p.a.
          </a>
          over the last 10 years.
          <i class="bi bi-info-circle info-icon" (click)="openInfoPopup('theme_backtest')"
           ></i>
        </p>
      </ng-container>

      <!-- NA stage: show accelerating text in gray -->
      <ng-container *ngIf="isNaStage">
        <p class="performance-sentence na-stage-text">
          Stocks of the
          <span class="sentence-link-disabled">top companies</span>
          that are most exposed to technologies at the
          <strong>Accelerating</strong>
          Stage have historically
          <span class="sentence-link-disabled">
            outperformed the market by +3.0% p.a.
          </span>
          over the last 10 years.
          <i class="bi bi-info-circle info-icon"></i>
        </p>
      </ng-container>
    </div>

    <!-- ── Score Breakdown card ── -->
    <div class="chart-card">
      <h5>
        Technology Lifecycle Overview
        <i class="bi bi-info-circle info-icon"
           (click)="openInfoPopup('theme_stages')"></i>
      </h5>

      <div class="lifecycle-row">
        <!-- 2x2 Quadrant with dynamic positioning -->
        <div class="quadrant-container">
          <svg viewBox="0 0 460 355" xmlns="http://www.w3.org/2000/svg">
            <!-- Y-axis label -->
            <text x="14" y="145" text-anchor="middle" fill="#6b7280" font-size="14" font-weight="600"
                  transform="rotate(-90 14 145)">Momentum</text>
            <!-- X-axis label -->
            <text x="265" y="295" text-anchor="middle" fill="#6b7280" font-size="14" font-weight="600">Acceleration</text>

            <!-- HIGH / LOW labels -->
            <text x="30" y="55" fill="#374151" font-size="14" font-weight="700">HIGH</text>
            <text x="30" y="235" fill="#374151" font-size="14" font-weight="700">LOW</text>
            <text x="75" y="275" fill="#6b7280" font-size="20" font-weight="700">&ndash;</text>
            <text x="445" y="275" fill="#6b7280" font-size="20" font-weight="700">+</text>

            <!-- Quadrant backgrounds (sized by midpoint lines) -->
            <!-- Top-left: Maturing (yellow) — High Momentum, Low Acceleration -->
            <rect x="75" y="30"
                  [attr.width]="getMidX() - 75" [attr.height]="getMidY() - 30" rx="6"
                  [attr.fill]="STAGE_BG_COLORS['Maturing']" />
            <!-- Top-right: Accelerating (green) — High Momentum, High Acceleration -->
            <rect [attr.x]="getMidX()" y="30"
                  [attr.width]="455 - getMidX()" [attr.height]="getMidY() - 30" rx="6"
                  [attr.fill]="STAGE_BG_COLORS['Accelerating']" />
            <!-- Bottom-left: Declining (red) — Low Momentum, Low Acceleration -->
            <rect x="75" [attr.y]="getMidY()"
                  [attr.width]="getMidX() - 75" [attr.height]="260 - getMidY()" rx="6"
                  [attr.fill]="STAGE_BG_COLORS['Declining']" />
            <!-- Bottom-right: Emerging (blue) — Low Momentum, High Acceleration -->
            <rect [attr.x]="getMidX()" [attr.y]="getMidY()"
                  [attr.width]="455 - getMidX()" [attr.height]="260 - getMidY()" rx="6"
                  [attr.fill]="STAGE_BG_COLORS['Emerging']" />

            <!-- Midpoint divider lines -->
            <line [attr.x1]="getMidX()" y1="30" [attr.x2]="getMidX()" y2="260"
                  stroke="#d1d5db" stroke-width="1" stroke-dasharray="4,3" />
            <line x1="75" [attr.y1]="getMidY()" x2="455" [attr.y2]="getMidY()"
                  stroke="#d1d5db" stroke-width="1" stroke-dasharray="4,3" />

            <!-- Quadrant labels -->
            <text [attr.x]="getQuadrantLabelPos('TL').x" [attr.y]="getQuadrantLabelPos('TL').y - 6"
                  text-anchor="middle" [attr.fill]="STAGE_COLORS['Maturing']" font-size="15" font-weight="600" font-style="italic">Stage 3</text>
            <text [attr.x]="getQuadrantLabelPos('TL').x" [attr.y]="getQuadrantLabelPos('TL').y + 12"
                  text-anchor="middle" [attr.fill]="STAGE_COLORS['Maturing']" font-size="15" font-weight="700">Maturing</text>

            <text [attr.x]="getQuadrantLabelPos('TR').x" [attr.y]="getQuadrantLabelPos('TR').y - 6"
                  text-anchor="middle" [attr.fill]="STAGE_COLORS['Accelerating']" font-size="15" font-weight="600" font-style="italic">Stage 2</text>
            <text [attr.x]="getQuadrantLabelPos('TR').x" [attr.y]="getQuadrantLabelPos('TR').y + 12"
                  text-anchor="middle" [attr.fill]="STAGE_COLORS['Accelerating']" font-size="15" font-weight="700">Accelerating</text>

            <text [attr.x]="getQuadrantLabelPos('BL').x" [attr.y]="getQuadrantLabelPos('BL').y - 6"
                  text-anchor="middle" [attr.fill]="STAGE_COLORS['Declining']" font-size="15" font-weight="600" font-style="italic">Stage 4</text>
            <text [attr.x]="getQuadrantLabelPos('BL').x" [attr.y]="getQuadrantLabelPos('BL').y + 12"
                  text-anchor="middle" [attr.fill]="STAGE_COLORS['Declining']" font-size="15" font-weight="700">Declining</text>

            <text [attr.x]="getQuadrantLabelPos('BR').x" [attr.y]="getQuadrantLabelPos('BR').y - 6"
                  text-anchor="middle" [attr.fill]="STAGE_COLORS['Emerging']" font-size="15" font-weight="600" font-style="italic">Stage 1</text>
            <text [attr.x]="getQuadrantLabelPos('BR').x" [attr.y]="getQuadrantLabelPos('BR').y + 12"
                  text-anchor="middle" [attr.fill]="STAGE_COLORS['Emerging']" font-size="15" font-weight="700">Emerging</text>

            <!-- Prior-year dot (solid grey) -->
            <ng-container *ngIf="getPriorYearDotPosition() as prior">
              <circle [attr.cx]="prior.x" [attr.cy]="prior.y"
                      r="7" fill="#9ca3af" stroke="#fff" stroke-width="2" />
            </ng-container>

            <!-- Theme dot (only when not NA) -->
            <ng-container *ngIf="getDotPosition() as dot">
              <circle [attr.cx]="dot.x" [attr.cy]="dot.y"
                      r="8" fill="#1f2937" stroke="#fff" stroke-width="2" />
            </ng-container>

            <!-- Legend (below x-axis label, one per line) -->
            <circle cx="170" cy="320" r="5" fill="#1f2937" />
            <text x="180" y="324" fill="#374151" font-size="13">{{ tab_name || 'Theme' }} (Now)</text>
            <circle cx="170" cy="343" r="5" fill="#9ca3af" />
            <text x="180" y="347" fill="#374151" font-size="13">1 Year Ago</text>
          </svg>
        </div>

        <!-- Table -->
        <div class="lifecycle-table-wrap">
          <table class="summary-table">
            <thead>
              <tr>
                <th></th>
                <th>{{ tab_name || 'Theme' }}</th>
                <th>All Tech Avg.</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="component-label">Size (10y)</td>
                <td class="value-cell">{{ formatNumber(data.cluster_stats.invention_count_120m) }}</td>
                <td class="value-cell">{{ formatNumber(data.descriptive_stats.invention_count_120m.mean) }}</td>
              </tr>
              <tr>
                <td class="component-label">Abs. Growth (1y)</td>
                <td class="value-cell">{{ formatNumber(data.cluster_stats.inv_growth_absolute_12m) }}</td>
                <td class="value-cell">{{ formatNumber(data.descriptive_stats.abs_growth_12m.mean) }}</td>
              </tr>
              <tr>
                <td class="component-label">Rel. Growth (1y, %)</td>
                <td class="value-cell">{{ formatPct(data.cluster_stats.inv_growth_relative_12m_sia) }}</td>
                <td class="value-cell">{{ formatPct(data.descriptive_stats.rel_growth_12m_sia_pct.mean) }}</td>
              </tr>
              <tr>
                <td class="component-label">Momentum</td>
                <td class="value-cell"
                    [class.text-high]="momentumLabel === 'High'"
                    [class.text-low]="momentumLabel === 'Low'">
                  {{ momentumLabel }}
                </td>
                <td class="value-cell"> - </td>
              </tr>
              <tr>
                <td class="component-label">Acceleration</td>
                <td class="value-cell"
                    [class.text-high]="accelerationLabel === 'High'"
                    [class.text-low]="accelerationLabel === 'Low'">
                  {{ accelerationLabel }}
                </td>
                <td class="value-cell"> - </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

  </ng-container>
</div>
